import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Menu,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TbDots, TbKey, TbPlus, TbShieldOff, TbTrash } from 'react-icons/tb'
import { notifyError, notifySuccess } from '@/frontend/lib/notify'
import {
  api,
  type CreateResponse,
  formatExpiry,
  formatRelative,
  SCOPE_COLOR,
  STATUS_COLOR,
  type TokenRow,
  type TokenScope,
} from './accesstokens/helpers'
import { openShowOnceModal } from './accesstokens/ShowOnceModal'

export function AccessTokensCard({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['project-tokens', projectId],
    queryFn: () => api<{ tokens: TokenRow[] }>(`/api/projects/${projectId}/access-tokens`),
    enabled: canManage,
  })

  const createToken = useMutation({
    mutationFn: (body: { name: string; scope: TokenScope; expiresInDays?: number }) =>
      api<CreateResponse>(`/api/projects/${projectId}/access-tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['project-tokens', projectId] })
      openShowOnceModal(res.raw, res.token.name)
      notifySuccess({ message: `Token "${res.token.name}" dibuat. Salin sekarang — hanya tampil sekali.` })
    },
    onError: (err) => notifyError(err),
  })

  const revokeToken = useMutation({
    mutationFn: (id: string) => api(`/api/projects/${projectId}/access-tokens/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-tokens', projectId] })
      notifySuccess({ message: 'Token di-revoke.' })
    },
    onError: (err) => notifyError(err),
  })

  const deleteToken = useMutation({
    mutationFn: (id: string) => api(`/api/projects/${projectId}/access-tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-tokens', projectId] })
      notifySuccess({ message: 'Token dihapus.' })
    },
    onError: (err) => notifyError(err),
  })

  const tokens = data?.tokens ?? []

  const openCreate = () => {
    let name = ''
    let scope: TokenScope = 'READ'
    let expiryPreset = 'never'
    modals.openConfirmModal({
      title: 'Buat access token',
      children: (
        <Stack gap="sm">
          <TextInput
            label="Nama"
            placeholder="mis. claude-code agent"
            onChange={(e) => (name = e.currentTarget.value)}
          />
          <Select
            label="Scope"
            defaultValue="READ"
            data={[
              { value: 'READ', label: 'READ — hanya baca' },
              { value: 'WRITE', label: 'WRITE — baca & update' },
            ]}
            onChange={(v) => (scope = (v as TokenScope) ?? 'READ')}
          />
          <Select
            label="Kedaluwarsa"
            defaultValue="never"
            data={[
              { value: 'never', label: 'Tidak ada' },
              { value: '7', label: '7 hari' },
              { value: '30', label: '30 hari' },
              { value: '90', label: '90 hari' },
              { value: '365', label: '1 tahun' },
            ]}
            onChange={(v) => (expiryPreset = v ?? 'never')}
          />
        </Stack>
      ),
      labels: { confirm: 'Buat', cancel: 'Batal' },
      confirmProps: { color: 'green' },
      onConfirm: () => {
        if (!name.trim()) return
        createToken.mutate({
          name: name.trim(),
          scope,
          expiresInDays: expiryPreset === 'never' ? undefined : Number(expiryPreset),
        })
      },
    })
  }

  const confirmRevoke = (t: TokenRow) =>
    modals.openConfirmModal({
      title: 'Revoke token',
      children: <Text size="sm">Token "{t.name}" tidak bisa dipakai lagi setelah di-revoke. Lanjutkan?</Text>,
      labels: { confirm: 'Revoke', cancel: 'Batal' },
      confirmProps: { color: 'red' },
      onConfirm: () => revokeToken.mutate(t.id),
    })

  const confirmDelete = (t: TokenRow) =>
    modals.openConfirmModal({
      title: 'Hapus token',
      children: <Text size="sm">Hapus token "{t.name}" permanen dari daftar?</Text>,
      labels: { confirm: 'Hapus', cancel: 'Batal' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteToken.mutate(t.id),
    })

  if (!canManage) return null

  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <TbKey size={18} />
          <Text fw={600} size="sm">
            Access Tokens
          </Text>
        </Group>
        <Button size="xs" leftSection={<TbPlus size={14} />} onClick={openCreate} loading={createToken.isPending}>
          Buat Token
        </Button>
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        Token per-project untuk agent coding (mis. Claude Code) agar bisa membaca/memperbarui task, bug, dan tiket
        project ini. WRITE = boleh update; READ = hanya baca.
      </Text>
      {tokens.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          Belum ada token.
        </Text>
      ) : (
        <Table striped highlightOnHover verticalSpacing="xs" fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nama</Table.Th>
              <Table.Th>Prefix</Table.Th>
              <Table.Th>Scope</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Dipakai</Table.Th>
              <Table.Th>Kedaluwarsa</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tokens.map((t) => (
              <Table.Tr key={t.id}>
                <Table.Td>{t.name}</Table.Td>
                <Table.Td>
                  <Code>{t.tokenPrefix}…</Code>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={SCOPE_COLOR[t.scope]} variant="light">
                    {t.scope}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={STATUS_COLOR[t.status]} variant="light">
                    {t.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatRelative(t.lastUsedAt)}</Table.Td>
                <Table.Td>{formatExpiry(t.expiresAt)}</Table.Td>
                <Table.Td>
                  <Menu shadow="md" position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <TbDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {t.status === 'ACTIVE' && (
                        <Menu.Item leftSection={<TbShieldOff size={14} />} color="red" onClick={() => confirmRevoke(t)}>
                          Revoke
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<TbTrash size={14} />} color="red" onClick={() => confirmDelete(t)}>
                        Hapus
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  )
}
